import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Building2, FileText, IndianRupee, Download, Printer,
  ArrowUpRight, ArrowDownRight, RefreshCw, Plus, Clock
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

export default function SupplierLedgerPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openingBalance, setOpeningBalance] = useState('');
  const [savingOB, setSavingOB] = useState(false);
  
  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers?is_active=true`, {
        headers: getAuthHeader()
      });
      // Sort suppliers alphabetically by name
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!selectedSupplier) {
      setLedgerData(null);
      return;
    }
    
    setLoading(true);
    try {
      let url = `${API_URL}/api/payment-requests/ledger/${selectedSupplier}`;
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      setLedgerData(response.data);
      setOpeningBalance(response.data.supplier?.opening_balance || '');
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedSupplier, fromDate, toDate]);

  useEffect(() => {
    if (selectedSupplier) {
      fetchLedger();
    }
  }, [selectedSupplier, fetchLedger]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    if (!ledgerData?.ledger?.length) return;
    
    const headers = ['Date', 'Document', 'Particulars', 'Debit', 'Credit', 'Balance'];
    const rows = ledgerData.ledger.map(entry => [
      entry.date,
      entry.document,
      entry.particulars,
      entry.debit || '',
      entry.credit || '',
      entry.balance
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger_${ledgerData.supplier?.name || 'supplier'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreateRequest = () => {
    // Navigate to payment requests page with supplier pre-selected
    navigate('/purchase/payment-requests', { 
      state: { 
        openCreateDialog: true, 
        supplierId: selectedSupplier,
        supplierName: ledgerData?.supplier?.name 
      } 
    });
  };

  const saveOpeningBalance = async () => {
    if (!selectedSupplier) return;
    setSavingOB(true);
    try {
      await axios.patch(
        `${API_URL}/api/suppliers/${selectedSupplier}/opening-balance`,
        { opening_balance: parseFloat(openingBalance) || 0 },
        { headers: getAuthHeader() }
      );
      toast.success('Opening balance updated');
      fetchLedger();
    } catch {
      toast.error('Failed to update opening balance');
    } finally {
      setSavingOB(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { className: 'bg-blue-100 text-blue-700', label: 'Submitted' },
      resubmitted: { className: 'bg-blue-100 text-blue-700', label: 'Resubmitted' },
      approved: { className: 'bg-green-100 text-green-700', label: 'Approved' },
      due: { className: 'bg-amber-100 text-amber-700', label: 'Due' },
      overdue: { className: 'bg-red-100 text-red-700', label: 'Overdue' },
      re_approved: { className: 'bg-green-100 text-green-700', label: 'Re-Approved' },
      modification_requested: { className: 'bg-purple-100 text-purple-700', label: 'Mod. Requested' },
    };
    const style = styles[status] || { className: 'bg-gray-100 text-gray-700', label: status };
    return <Badge variant="outline" className={style.className}>{style.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="supplier-ledger-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Supplier Ledger</h1>
            <p className="text-muted-foreground">View supplier-wise transaction history</p>
          </div>
          {selectedSupplier && (
            <Button onClick={handleCreateRequest}>
              <Plus className="w-4 h-4 mr-2" />
              Create Payment Request
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[250px]">
                <Label className="mb-2 block">Select Supplier *</Label>
                <SearchableSelect
                  options={suppliers.map(s => ({ value: s.id, label: s.name })).sort((a, b) => a.label.localeCompare(b.label))}
                  value={selectedSupplier}
                  onValueChange={setSelectedSupplier}
                  placeholder="Choose a supplier..."
                  searchPlaceholder="Search suppliers..."
                  emptyText="No suppliers found"
                />
              </div>
              
              <div className="w-[160px]">
                <Label className="mb-2 block">From Date</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              
              <div className="w-[160px]">
                <Label className="mb-2 block">To Date</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <Button variant="outline" onClick={fetchLedger} disabled={!selectedSupplier}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              {ledgerData && (
                <>
                  <Button variant="outline" onClick={handlePrint}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                  <Button variant="outline" onClick={handleExportCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Supplier Info & Summary */}
        {ledgerData && (
          <>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-semibold">{ledgerData.supplier?.name}</p>
                    {ledgerData.supplier?.city && (
                      <p className="text-sm text-muted-foreground">{ledgerData.supplier.city}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Opening Balance</Label>
                  <Input
                    type="number"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    className="w-40 text-right"
                    placeholder="0"
                    data-testid="supplier-opening-balance"
                  />
                  <Button
                    size="sm"
                    onClick={saveOpeningBalance}
                    disabled={savingOB}
                    data-testid="save-supplier-ob"
                  >
                    {savingOB ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-muted-foreground">Total Invoices</span>
                </div>
                <p className="text-xl font-bold">{formatCurrency(ledgerData.summary?.total_invoices)}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">Total Payments</span>
                </div>
                <p className="text-xl font-bold">{formatCurrency(ledgerData.summary?.total_payments)}</p>
              </CardContent>
            </Card>
            
            <Card className={ledgerData.summary?.closing_balance > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <IndianRupee className={`w-5 h-5 ${ledgerData.summary?.closing_balance > 0 ? 'text-red-500' : 'text-green-500'}`} />
                  <span className="text-sm text-muted-foreground">Closing Balance</span>
                </div>
                <p className={`text-xl font-bold ${ledgerData.summary?.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(ledgerData.summary?.closing_balance)}
                </p>
              </CardContent>
            </Card>
          </div>
        </>
        )}

        {/* Pending Payment Requests */}
        {ledgerData?.pending_requests?.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                <Clock className="w-5 h-5" />
                Pending Payment Requests ({ledgerData.pending_requests.length})
              </CardTitle>
              <CardDescription>
                Total pending: {formatCurrency(ledgerData.summary?.pending_payment_requests)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request No.</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerData.pending_requests.map(req => (
                    <TableRow key={req.id}>
                      <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                      <TableCell className="capitalize">{req.payment_type?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(req.approved_amount || req.amount_requested)}
                      </TableCell>
                      <TableCell>{formatDate(req.approved_payment_date || req.payment_due_date)}</TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Ledger Table */}
        {!selectedSupplier ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Building2 className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Select a supplier to view their ledger</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Loading ledger...
            </CardContent>
          </Card>
        ) : ledgerData?.ledger?.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>No transactions found for this supplier</p>
            </CardContent>
          </Card>
        ) : ledgerData && (
          <Card id="ledger-print-area">
            <CardHeader>
              <CardTitle>Transaction Ledger</CardTitle>
              <CardDescription>
                {ledgerData.period?.from && ledgerData.period?.to
                  ? `Period: ${formatDate(ledgerData.period.from)} to ${formatDate(ledgerData.period.to)}`
                  : 'All transactions'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead className="w-36">Document</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead className="text-right w-32">Debit</TableHead>
                    <TableHead className="text-right w-32">Credit</TableHead>
                    <TableHead className="text-right w-36">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledgerData.ledger.map((entry, index) => (
                    <TableRow key={index} className={entry.type === 'payment' ? 'bg-green-50' : entry.type === 'opening_balance' ? 'bg-amber-50 font-semibold' : ''}>
                      <TableCell className="font-mono text-sm">{formatDate(entry.date)}</TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{entry.document}</span>
                      </TableCell>
                      <TableCell className="text-sm">{entry.particulars}</TableCell>
                      <TableCell className="text-right font-mono">
                        {entry.debit > 0 ? (
                          <span className="text-red-600">{formatCurrency(entry.debit)}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {entry.credit > 0 ? (
                          <span className="text-green-600">{formatCurrency(entry.credit)}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${entry.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(entry.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Totals Row */}
                  <TableRow className="bg-muted font-semibold border-t-2">
                    <TableCell colSpan={3} className="text-right">TOTALS</TableCell>
                    <TableCell className="text-right text-red-600">
                      {formatCurrency(ledgerData.summary?.total_invoices)}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(ledgerData.summary?.total_payments)}
                    </TableCell>
                    <TableCell className={`text-right ${ledgerData.summary?.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(ledgerData.summary?.closing_balance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #ledger-print-area, #ledger-print-area * {
            visibility: visible;
          }
          #ledger-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </DashboardLayout>
  );
}
